import styled from '@emotion/styled';
import { Button, Form, Radio } from 'antd';
import FormField, { SmartFieldProps } from 'components/FormField';
import { useFormikContext } from 'formik';
import dayjs from 'lib/dayjs';
import { useTranslate } from '@tolgee/react';
import { PolicyFormValues } from '../NewPolicy/share';
import { DatePicker } from 'components/DatePicker';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { FormOutlined } from '@ant-design/icons';

type FV = Pick<PolicyFormValues, 'termOfValidity' | 'knowledgeId'>;

export const Alert = styled.div`
  display: inline-block;
  height: 32px;
  line-height: 32px;
  padding: 0 16px;
  margin-bottom: 2px;
  background: var(--fett-surface-warning-bg);
`;

const NoWrap = styled.div`
  white-space: nowrap;
`;

const InlineItem = styled.span`
  display: inline-block;
  padding: 0 8px;
  .${({ theme }) => theme.getAntdPrefixedClassName('form-item')} {
    width: 90px;
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('form-item-explain')} {
    white-space: nowrap;
  }
`;

export interface TermOfValidityFieldProps {
  scenarioType: Marketing.AllScenarioType;
  disabled?: boolean;
  region?: General.UpperCaseRegion;
}

function TermOfValidityField(props: TermOfValidityFieldProps) {
  const { scenarioType, disabled: disabledProp = false, region } = props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const {
    values: {
      termOfValidity: { type },
      knowledgeId,
    },
  } = useFormikContext<FV>();

  const isDelayShip = scenarioType === 'DELAY_SHIP';

  const disabled = disabledProp || Boolean(knowledgeId);

  return (
    <Form.Item
      required
      label={isDelayShip ? t('aide.限定买家下单时间') : t('marketing.有效期')}
      style={
        type !== 'LONG_TERM' ? { marginBottom: 0, transition: 'none' } : { transition: 'none' }
      }
    >
      <Alert>
        {region
          ? t('marketing.任务执行时间以当前region站点时区为准', {
              region: getRegionLabel(region),
            })
          : t('aide.任务执行时间以站点时区为准')}
      </Alert>
      <FormField name="termOfValidity.type" noStyle>
        {({ field: { value, onChange } }: SmartFieldProps<FV['termOfValidity']['type']>) => {
          const radios = (
            <Radio.Group
              disabled={disabled}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            >
              <Radio value="LONG_TERM">{isDelayShip ? t('aide.不限') : t('marketing.长期')}</Radio>
              <Radio value="DATE_TIME_QUANTUM">{t('marketing.起止')}</Radio>
              <Radio
                value={value === 'MULTI_TIME_QUANTUM' ? 'MULTI_TIME_QUANTUM' : 'TIME_QUANTUM_GROUP'}
              >
                {t('marketing.固定时段')}
              </Radio>
            </Radio.Group>
          );

          return <div className={type !== 'LONG_TERM' ? 'mb-[6px]' : ''}>{radios}</div>;
        }}
      </FormField>
      {type === 'DATE_TIME_QUANTUM' && (
        <FormField<FV['termOfValidity']['dateTimeQuantum']>
          name="termOfValidity.dateTimeQuantum"
          validate={(value) => {
            const { startDateTime, endDateTime } = value || {};

            if (!startDateTime || !endDateTime) {
              return t('marketing.请选择起止时间');
            }

            if (dayjs(endDateTime).isBefore(dayjs())) {
              return t('marketing.结束时间不可早于当前时间');
            }

            return undefined;
          }}
        >
          {({ field: { value, onChange, ...others } }) => {
            const { startDateTime, endDateTime } = value || {};

            return (
              <DatePicker.RangePicker
                disabled={disabled}
                showTime
                format="YYYY-MM-DD HH:mm"
                value={[
                  startDateTime ? dayjs(startDateTime) : null,
                  endDateTime ? dayjs(endDateTime) : null,
                ]}
                onChange={(range) => {
                  const [start, end] = range || [];
                  onChange({
                    startDateTime: start ? dayjs(start).format('YYYY-MM-DD HH:mm') : null,
                    endDateTime: end ? dayjs(end).format('YYYY-MM-DD HH:mm') : null,
                  });
                }}
                disabledDate={(currentDate) => {
                  return currentDate.isBefore(dayjs().subtract(1, 'days').startOf('days'));
                }}
                {...others}
              />
            );
          }}
        </FormField>
      )}
      {type === 'TIME_QUANTUM_GROUP' && (
        <FormField<FV['termOfValidity']['timeQuantumGroup']>
          name="termOfValidity.timeQuantumGroup"
          validate={(value) => {
            const { startDate, endDate, startTime, endTime } = value || {};

            if (!startDate || !endDate || !startTime || !endTime) {
              return t('marketing.请选择固定时间段');
            }

            if (
              startDate === endDate &&
              dayjs(startTime, 'HH:mm:ss').isAfter(dayjs(endTime, 'HH:mm:ss'))
            ) {
              return t('marketing.跨天任务日期范围需大于一天');
            }

            if (dayjs(`${endDate} ${endTime}`).isBefore(dayjs())) {
              return t('marketing.结束时间不可早于当前时间');
            }

            return undefined;
          }}
        >
          {({ field: { value, onChange, onBlur } }) => {
            const { startDate, endDate, startTime, endTime } = value || {};
            return (
              <NoWrap onBlur={onBlur}>
                {t('marketing.起止日期')}
                <InlineItem>
                  <DatePicker.RangePicker
                    disabled={disabled}
                    value={[startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null]}
                    onChange={(range) => {
                      const [start, end] = range || [];
                      onChange({
                        ...value,
                        startDate: start ? dayjs(start).format('YYYY-MM-DD') : null,
                        endDate: end ? dayjs(end).format('YYYY-MM-DD') : null,
                      });
                    }}
                    disabledDate={(currentDate) => {
                      return currentDate.isBefore(dayjs().subtract(1, 'days').startOf('days'));
                    }}
                  />
                </InlineItem>
                {t('marketing.每天')}
                <InlineItem>
                  <DatePicker.RangePicker
                    picker="time"
                    mode={undefined}
                    disabled={disabled}
                    order={false}
                    format="HH:mm"
                    value={[
                      startTime ? dayjs(startTime, 'HH:mm:ss') : null,
                      endTime ? dayjs(endTime, 'HH:mm:ss') : null,
                    ]}
                    onChange={(range) => {
                      const [start, end] = range || [];
                      onChange({
                        ...value,
                        startTime: start
                          ? dayjs(start).startOf('minutes').format('HH:mm:ss')
                          : null,
                        endTime: end ? dayjs(end).startOf('minutes').format('HH:mm:ss') : null,
                      });
                    }}
                  />
                </InlineItem>
              </NoWrap>
            );
          }}
        </FormField>
      )}
      {type === 'MULTI_TIME_QUANTUM' && (
        <FormField<FV['termOfValidity']['multiTimeQuantum']> name="termOfValidity.multiTimeQuantum">
          {({ field: { value } }) => {
            const { startDate, endDate, timeQuantum = [] } = value || {};
            return (
              <NoWrap>
                {t('marketing.起止日期')}
                <InlineItem>
                  <DatePicker.RangePicker
                    disabled={disabled}
                    value={[startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null]}
                    format="YYYY-MM-DD"
                  />
                </InlineItem>
                {t('marketing.每天')}
                <InlineItem
                  style={{ marginLeft: '12px', color: 'rgba(0,0,0,.25)', background: '#f5f5f5' }}
                >
                  {timeQuantum.map(({ startTime, endTime }) => `${startTime}-${endTime}`).join(' ')}
                  <Button disabled type="text" icon={<FormOutlined />} />
                </InlineItem>
              </NoWrap>
            );
          }}
        </FormField>
      )}
    </Form.Item>
  );
}

export default TermOfValidityField;
